import {
  ISelectOption,
  ISelectOptionWithChildren,
  Modal,
  Select,
  SelectTree,
  Button,
  Input,
  SvgIconButton,
  SvgIcon,
  Alert,
  TooltipComponent,
} from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { SegmentationOptionsFactory } from './SegmentationOptionsFactory';
import { useFeatureFlags } from 'core/app/AppContext';
import { renderStringFilterValue } from 'sections/offsite-campaigns/offsite-campaign-editor/components/editor/segment-item/segment-filter-items/utils/helpers';

const SegmentationRow = styled.div`
  margin-bottom: ${({ theme }) => theme.designToken.spacing4};
`;

const AlertContainer = styled.div``;

const Rules = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 84px;
  background-color: ${({ theme }) => theme.designToken.neutral50};
  padding: 16px 24px 16px 24px;
  margin-bottom: 20px;
`;

const RulesSelectAFilter = styled.div`
  background-color: ${({ theme }) => theme.designToken.neutral50};
  padding: 16px 24px 16px 24px;

  display: grid;
  grid-template-columns: 202px 99px;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  height: 100%;
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 65px;
  align-items: center;
  height: 52;
`;

const DropdownRuleWrapper = styled.div`
  flex-grow: 1;
  max-width: 262px;
  & > div {
    width: 262px;
  }
`;

const DropdownOperatorAndValueWrapper = styled.div`
  flex-grow: 1;
  max-width: 180px;
  & > div {
    width: 180px;
  }
`;

const ValueInput = styled.div`
  flex-grow: 1;
  & > div {
    width: 100%;
  }
`;

const DropdownsContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  gap: 24px;
`;

const BasketTypeAlert = styled.div`
  margin-top: ${({ theme }) => theme.designToken.spacing4};
`;

const SegmentationButton = ({ type, onClick, disable }: { type: string; onClick: () => void; disable: boolean }) => {
  const handleOnClick = useCallback(() => {
    if (disable) {
      onClick();
    }
  }, [disable, onClick]);

  const icon = useMemo(() => (!disable ? 'check' : 'plus'), [disable]);

  return (
    <Button
      onClick={handleOnClick}
      disabled={!disable}
      leftSideIcon={type === 'AND' && <SvgIcon name={icon} />}
      variation="segmentationButton"
      aria-label={`${type} operator`}
    >
      {type}
    </Button>
  );
};

export type SegmentationRuleConfiguration<TCondition, TValue> = {
  condition: TCondition;
  value: TValue;
};

export type SegmentationRule<TType, TCondition, TValue> = {
  type: TType;
  configuration: SegmentationRuleConfiguration<TCondition, TValue>;
};

export interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (rules: SegmentationRule<any, any, any>[][]) => void;
  initialRules: SegmentationRule<any, any, any>[][];
  okButtonLabel?: string;
  heading?: string;
  optionsFactory: SegmentationOptionsFactory;
  /** 
    Multiple rules per AND section are not allowed if false.
    NOTE: This will be implemented in future work once the OR functionality has been added.
  */
  hasOROption?: boolean;
  hasInListFunctionalty?: boolean;
}

export function SegmentationModal({
  open,
  onClose,
  onSave,
  initialRules,
  okButtonLabel = 'Save',
  heading = 'Add segmentation filters for your campaign', // TODO bring it in from translation file
  optionsFactory,
  hasOROption = false,
  hasInListFunctionalty = false,
  ...props
}: Props) {
  const { t } = useTranslation('campaigns');
  const { flagEnabled } = useFeatureFlags();

  const [rules, setRules] = useState<SegmentationRule<any, any, any>[][]>([[]]);

  const isLegacy = (): boolean => {
    if (!rules || rules.length < 1) {
      return false;
    }

    const lagacy = filterByType(rules, 'legacy');
    const sendOverride = filterByType(rules, 'sendOverride');

    return lagacy.length >= 1 || sendOverride.length >= 1;
  };

  const isBasketType = (): boolean => {
    if (!rules || rules.length < 1) {
      return false;
    }

    const filteredData = filterByType(rules, 'basketType');
    return filteredData.length >= 1;
  };

  const filterByType = (rules: SegmentationRule<any, any, any>[][], type: string) => {
    if (Array.isArray(rules)) {
      return rules.filter((rule: SegmentationRule<any, any, any>[]) => {
        if (Array.isArray(rule)) {
          return rule.some((item: SegmentationRule<any, any, any>) => item.type === type);
        } else {
          return [];
        }
      });
    }
  };

  useEffect(() => {
    if (initialRules?.length && open) {
      const reference = initialRules?.map(AND => AND.map(OR => ({ ...OR })));
      setRules(reference ?? [[]]);
    } else {
      setRules([[]]);
    }
  }, [initialRules, open]);

  const hasNotChange = useMemo(() => {
    let isSame = true;

    if (rules.length !== initialRules?.length) {
      return false;
    }

    if (rules.flatMap(s => s).length !== initialRules?.flatMap(s => s).length) {
      return false;
    }

    rules.forEach((ANDs, ANDIndex) => {
      if (!isSame) {
        return;
      }

      ANDs.forEach((OR, ORIndex) => {
        if (!isSame) {
          return;
        }

        const initialOR = initialRules[ANDIndex][ORIndex];
        if (!initialOR) {
          isSame = false;
          return;
        }

        isSame =
          OR.type === initialOR.type &&
          OR.configuration?.condition === initialOR.configuration?.condition &&
          OR.configuration?.value === initialOR.configuration?.value;
      });
    });

    return isSame;
  }, [rules, initialRules]);

  const onTypeChanged = useCallback(
    (ANDIndex: number, ORIndex: number) => (e: ISelectOptionWithChildren) => {
      const newRules = rules.map(AND => AND.map(OR => ({ ...OR })));
      const { operator, value } = optionsFactory.defaultValuesSelector(e.value);

      if (!newRules[ANDIndex][ORIndex]) {
        const initialRule = {
          type: e.value,
          configuration: {
            condition: operator.value,
            value: value.value,
          },
        };

        newRules[ANDIndex].push(initialRule);
      } else {
        newRules[ANDIndex][ORIndex] = {
          type: e.value,
          configuration: {
            condition: operator.value,
            value: value.value,
          },
        };
      }

      setRules(newRules);
    },
    [optionsFactory, rules],
  );

  const onOperatorChanged = useCallback(
    (ANDIndex: number, ORIndex: number) => (e: ISelectOption<string>) => {
      const newRules = rules.map(AND => AND.map(OR => ({ ...OR })));

      newRules[ANDIndex][ORIndex] = {
        ...newRules[ANDIndex][ORIndex],
        configuration: {
          ...newRules[ANDIndex][ORIndex].configuration,
          condition: e.value,
        },
      };

      setRules(newRules);
    },
    [rules],
  );

  const onValueChanged = useCallback(
    (ANDIndex: number, ORIndex: number) => (e: ISelectOption<string> | string) => {
      const newRules = rules.map(AND => AND.map(OR => ({ ...OR })));

      const value = typeof e === 'string' ? e : e.value;

      newRules[ANDIndex][ORIndex] = {
        ...newRules[ANDIndex][ORIndex],
        configuration: {
          ...newRules[ANDIndex][ORIndex].configuration,
          value,
        },
      };

      setRules(newRules);
    },
    [rules],
  );

  const onRemove = useCallback(
    (ANDIndex: number, ORIndex: number) => () => {
      const newRules = rules.map(AND => AND.map(OR => ({ ...OR })));
      newRules[ANDIndex].splice(ORIndex, 1);
      const isLastOR = !newRules[ANDIndex].length;
      if (isLastOR) {
        newRules.splice(ANDIndex, 1);
      }
      const isNoRules = !newRules.length;
      if (isNoRules) {
        setRules([[]]);
      } else {
        setRules([...newRules]);
      }
    },
    [rules],
  );

  const handleSave = useCallback(() => {
    onClose();
    onSave(rules);
  }, [onClose, onSave, rules]);

  const handleClose = useCallback(() => {
    onClose();
    const newRules = initialRules.length ? initialRules : [[]];
    setRules(newRules);
  }, [initialRules, onClose]);

  const onANDClicked = useCallback(
    (ANDIndex: number) => () => {
      setRules([...rules, []]);
    },
    [rules],
  );

  const getInputByFilterType = useCallback(
    (ANDIndex: number, ORIndex: number, OR: SegmentationRule<any, any, any>) => {
      const inputType = optionsFactory.inputTypeSelector(OR.type);
      switch (inputType) {
        case 'string':
          return (
            renderStringFilterValue(OR.configuration.condition) && (
              <ValueInput>
                <Input
                  ariaLabel={'Segmentation Value'}
                  value={OR.configuration.value}
                  onChange={onValueChanged(ANDIndex, ORIndex)}
                  spacingBelow={false}
                  minWidth="100%"
                />
              </ValueInput>
            )
          );
        case 'number':
          return (
            <ValueInput>
              <Input
                ariaLabel={'Segmentation Value'}
                value={OR.configuration.value}
                type={'number'}
                onChange={onValueChanged(ANDIndex, ORIndex)}
                spacingBelow={false}
                minWidth="100%"
              />
            </ValueInput>
          );
        default: {
          return (
            <ValueInput>
              <Select
                ariaLabel={'Segmentation Value'}
                options={optionsFactory.valuesSelector(OR.type)}
                onChange={onValueChanged(ANDIndex, ORIndex)}
                value={optionsFactory.valueOptionCreator(OR.type, OR.configuration?.value)}
                spacingBelow={false}
              />
            </ValueInput>
          );
        }
      }
    },
    [onValueChanged, optionsFactory],
  );

  const onORClick = useCallback(
    (ANDIndex: number, ORIndex: number) => () => {
      rules[ANDIndex].splice(ORIndex + 1, 0, [] as any);
      setRules([...rules]);
    },
    [rules],
  );
  const filterOptions = options => {
    if (hasOROption && !flagEnabled('ld-onsite-basket-segmentation')) {
      return options.filter(item => item.value !== 'eCommerce');
    } else {
      return options;
    }
  };

  const footerAlert = isLegacy() || isBasketType();

  return (
    <>
      <Modal
        minWidth={1084}
        heading={heading}
        isOpen={open}
        variation="clean"
        okButtonType={'primary'}
        okButtonAriaLabel={'Save Button Segmentation Modal'}
        okButtonLabel={'Save'} // TODO bring in text from a modal specific file with translation
        onOkButtonClicked={handleSave}
        okButtonDisabled={hasNotChange}
        onCloseButtonClicked={handleClose}
        subHeader={
          <div style={{ display: 'flex' }}>
            <p style={{ marginRight: '5px' }}>
              {t('onsite.editor.workflow.triggerMessage.drawer.segmentationModal.subHeader')}
            </p>
            {hasInListFunctionalty && (
              <TooltipComponent
                title={t('onsite.editor.workflow.triggerMessage.drawer.segmentationModal.inListTooltipTitle')}
                label={t('onsite.editor.workflow.triggerMessage.drawer.segmentationModal.inListTooltipLabel')}
                position="Right"
              >
                <SvgIcon size="16" name="circle-question" id="circle-question" />
              </TooltipComponent>
            )}
          </div>
        }
        // Setting the overflow to visible allow the dropdown menus to appear outside their container.
        overflow="visible"
        footerAlert={
          footerAlert && (
            <AlertContainer>
              {isLegacy() && (
                <Alert
                  variation="info"
                  heading={t('onsite.editor.workflow.triggerMessage.drawer.segmentationModal.legacyAlert')}
                ></Alert>
              )}
              {isBasketType() && (
                <BasketTypeAlert>
                  <Alert
                    variation="info"
                    heading={t('onsite.editor.workflow.triggerMessage.drawer.segmentationModal.basketAlert')}
                  ></Alert>
                </BasketTypeAlert>
              )}
            </AlertContainer>
          )
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {rules.map((ANDs, ANDIndex) => (
            <div aria-label="AND section" key={ANDIndex}>
              <SegmentationRow>
                {ANDs.length ? (
                  ANDs.map((OR, ORIndex) => (
                    <Rules key={`rules-${ANDIndex}-${ORIndex}`}>
                      <DropdownsContainer>
                        <DropdownRuleWrapper>
                          <SelectTree
                            ariaLabel={'Segmentation Type'}
                            options={optionsFactory.ruleTypeOptions}
                            onChange={onTypeChanged(ANDIndex, ORIndex)}
                            value={optionsFactory.ruleOptionCreator(OR.type)}
                            placeholder={'Select a filter'}
                            spacingBelow={false}
                          />
                        </DropdownRuleWrapper>
                        {OR?.configuration?.condition && (
                          <DropdownOperatorAndValueWrapper>
                            <Select
                              ariaLabel={'Segmentation Condition'}
                              options={optionsFactory.operatorsSelector(OR.type)}
                              onChange={onOperatorChanged(ANDIndex, ORIndex)}
                              value={optionsFactory.operatorOptionCreator(OR.configuration.condition, OR.type)}
                              spacingBelow={false}
                              searchable={false}
                            />
                          </DropdownOperatorAndValueWrapper>
                        )}
                        {OR?.type && getInputByFilterType(ANDIndex, ORIndex, OR)}
                      </DropdownsContainer>
                      {hasOROption && (
                        <ButtonContainer>
                          <SegmentationButton
                            type="OR"
                            onClick={onORClick(ANDIndex, ORIndex)}
                            disable={!rules[ANDIndex][ORIndex + 1]}
                          />
                        </ButtonContainer>
                      )}
                      {OR && (
                        <ButtonContainer>
                          <SvgIconButton
                            onClick={onRemove(ANDIndex, ORIndex)}
                            ariaLabel={t(`onsite.delete`)}
                            name="trash"
                            size="32"
                          />
                        </ButtonContainer>
                      )}
                    </Rules>
                  ))
                ) : (
                  <RulesSelectAFilter key={`empty-rules-${ANDIndex}`}>
                    <DropdownsContainer>
                      <DropdownRuleWrapper>
                        <SelectTree
                          ariaLabel={'Segmentation Type'}
                          options={filterOptions(optionsFactory.ruleTypeOptions)}
                          onChange={onTypeChanged(ANDIndex, 0)}
                          placeholder={'Select a filter'}
                          spacingBelow={false}
                        />
                      </DropdownRuleWrapper>
                    </DropdownsContainer>
                    {ANDIndex > 0 && (
                      <ButtonContainer>
                        <SvgIconButton
                          onClick={onRemove(ANDIndex, 0)}
                          ariaLabel={t(`onsite.delete`)}
                          name="trash"
                          size="32"
                        />
                      </ButtonContainer>
                    )}
                  </RulesSelectAFilter>
                )}
              </SegmentationRow>
              <SegmentationButton type="AND" onClick={onANDClicked(ANDIndex)} disable={!rules[ANDIndex + 1]} />
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
}
